import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, ArrowLeft, Check, ShieldCheck, Sparkles, Mail, Phone, Clock,
  Instagram, Facebook, Youtube, Search, Globe, Users2, Loader2, PartyPopper,
  Sun, Coffee, Moon, CalendarDays, Briefcase,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const emptyData = {
  name: "", quelle: "", empfehlung_name: "", alter: "", beruf: "",
  sicherheit: "", bereiche: [], wichtig: [], motivation: [],
  schon_getan: [], investments: [],
  einmalig_chf: "", monatlich_chf: "",
  email: "", telefon: "", erreichbar_zeitspanne: "",
  anmerkungen: "", gp_name_query: "", gp_id: "",
  datenschutz_akzeptiert: false, email_kontakt_ok: false,
};

const QUELLE_OPTS = [
  { v: "Instagram", label: "Instagram", icon: Instagram },
  { v: "Facebook", label: "Facebook", icon: Facebook },
  { v: "Youtube", label: "Youtube", icon: Youtube },
  { v: "Google", label: "Google", icon: Search },
  { v: "Webseite", label: "Webseite", icon: Globe },
  { v: "Empfehlung", label: "Empfehlung", icon: Users2 },
];
const SICHERHEIT_OPTS = [
  { v: "Überhaupt nicht sicher", color: "bg-red-500" },
  { v: "Eher unsicher", color: "bg-orange-500" },
  { v: "Neutral", color: "bg-yellow-500" },
  { v: "Eher sicher", color: "bg-emerald-500" },
  { v: "Sehr sicher", color: "bg-sky-500" },
];
const BEREICHE_OPTS = [
  "Vorsorge", "Investitionen", "Steuern",
  "Finanzielle Bildung & Klarheit", "Vermögensaufbau", "KI & Trading",
];
const WICHTIG_OPTS = [
  "💰 Sicherheit", "📈 Vermögensaufbau", "🗂️ Klarheit & Struktur",
  "🌅 Frühzeitiger Ruhestand", "💸 Steueroptimierung", "🏠 Mietzinskonto vergolden",
];
const MOTIVATION_OPTS = [
  "🕊️ Mehr Freiheit", "👨‍👩‍👧‍👦 Absicherung der Familie",
  "🏖️ Ruhestand absichern", "💳 Schulden loswerden", "🏠 Eigentum erwerben",
];
const GETAN_OPTS = [
  "Ich spare regelmässig", "Ich habe eine 3. Säule", "Ich habe ein Eigenheim",
  "Ich investiere mein Geld", "Ich bin selbstständig und baue ein Unternehmen auf",
  "Noch nichts Konkretes",
];
const INVEST_OPTS = [
  "Edelmetalle", "Immobilien", "Aktien / Fonds", "Kryptowährungen", "Andere / Keine",
];
const ERREICHBAR_OPTS = [
  { v: "Vormittags (9–11 Uhr)", label: "Vormittags", sub: "9 – 11 Uhr", icon: Coffee },
  { v: "Mittags (12–14 Uhr)", label: "Mittags", sub: "12 – 14 Uhr", icon: Sun },
  { v: "Abends (17–20 Uhr)", label: "Abends", sub: "17 – 20 Uhr", icon: Moon },
  { v: "Am Wochenende", label: "Am Wochenende", sub: "Sa / So", icon: CalendarDays },
];

const inputCls =
  "w-full rounded-2xl border border-ink-700/15 bg-ivory-100 text-ink-900 px-5 py-3.5 text-base focus:outline-none focus:ring-2 focus:ring-gold-500/40 focus:border-gold-500 transition placeholder:text-ink-500/50";

const Chip = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`text-left rounded-2xl border-2 px-5 py-4 transition-all ${active
      ? "border-gold-500 bg-ink-900 text-ivory-100 shadow-[0_6px_20px_-8px_rgba(201,169,97,0.6)]"
      : "border-ink-700/10 bg-white hover:border-gold-500/40 hover:bg-ivory-100"}`}>
    {children}
  </button>
);

const IconChip = ({ active, onClick, icon: Icon, label, sub, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`flex flex-col items-center justify-center gap-1.5 rounded-2xl border-2 px-5 py-5 transition-all ${active
      ? "border-gold-500 bg-ink-900 text-gold-300 shadow-[0_6px_20px_-8px_rgba(201,169,97,0.6)]"
      : "border-ink-700/10 bg-white text-ink-700 hover:border-gold-500/40"}`}>
    <Icon className="w-5 h-5" />
    <span className="text-sm font-medium">{label}</span>
    {sub && <span className={`text-xs ${active ? "text-gold-300/70" : "text-ink-500"}`}>{sub}</span>}
  </button>
);

const Progress = ({ step, total }) => (
  <div className="flex items-center gap-2">
    <div className="flex-1 h-1.5 rounded-full bg-ink-700/10 overflow-hidden">
      <motion.div className="h-full rounded-full"
        style={{ background: "linear-gradient(90deg,#C9A961,#E0C77A)" }}
        initial={false} animate={{ width: `${(step / total) * 100}%` }}
        transition={{ duration: 0.4 }} />
    </div>
    <span className="text-xs uppercase tracking-widest text-ink-500 font-semibold tabular-nums">{step} / {total}</span>
  </div>
);

const Q = ({ children }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
    className="space-y-6">{children}</motion.div>
);
const QTitle = ({ children }) => (
  <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight leading-[1.15] text-ink-900">{children}</h2>
);
const QHint = ({ children }) => (
  <p className="text-ink-500 text-sm sm:text-base leading-relaxed">{children}</p>
);

const PartnerPicker = ({ data, set, nameField = "gp_name_query", idField = "gp_id", placeholder = "z. B. Pascal Schwab", testIdBase = "fc-gp" }) => {
  const [q, setQ] = useState(data[nameField] || "");
  const [suggestions, setSuggestions] = useState([]);
  const [chosen, setChosen] = useState(null);
  useEffect(() => {
    let alive = true;
    if (q.trim().length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/api/finanzcheck/partners`, { params: { q } });
        if (alive) setSuggestions(r.data.partners || []);
      } catch {}
    }, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);
  return (
    <div className="space-y-3">
      <input className={inputCls} placeholder={placeholder} autoFocus
        value={q} onChange={(e) => { setQ(e.target.value); set(nameField, e.target.value); setChosen(null); set(idField, ""); }}
        data-testid={`${testIdBase}-search`} />
      {suggestions.length > 0 && !chosen && (
        <div className="rounded-2xl border border-ink-700/10 bg-white overflow-hidden divide-y divide-ink-700/5">
          {suggestions.map((p) => (
            <button key={p.id} type="button"
              onClick={() => { setChosen(p); set(idField, p.id); set(nameField, p.name); setQ(p.name); setSuggestions([]); }}
              className="w-full text-left px-5 py-3 hover:bg-ivory-100 flex items-center justify-between gap-3"
              data-testid={`${testIdBase}-pick-${p.id}`}>
              <span className="font-medium text-ink-900">{p.name}</span>
              <span className="text-xs text-ink-500">{p.email}</span>
            </button>
          ))}
        </div>
      )}
      {chosen && (
        <div className="rounded-2xl bg-ink-900 text-ivory-100 px-5 py-3 flex items-center gap-3">
          <Check className="w-4 h-4 text-gold-300" />
          <span className="text-sm">Ausgewählt: <strong className="text-gold-300">{chosen.name}</strong></span>
        </div>
      )}
    </div>
  );
};

export default function Finanzcheck() {
  const [params] = useSearchParams();
  const routeParams = useParams();
  const gpFromUrl = routeParams.gp || params.get("gp") || "";
  const [prefilledPartnerName, setPrefilledPartnerName] = useState("");
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ ...emptyData, gp_id: gpFromUrl });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggle = (k, v) => setData((d) => ({
    ...d, [k]: d[k].includes(v) ? d[k].filter((x) => x !== v) : [...d[k], v],
  }));

  // If ?gp=<id> in URL, resolve the partner's name once
  useEffect(() => {
    if (!gpFromUrl) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/finanzcheck/partner/${gpFromUrl}`);
        if (r.data?.name) {
          setPrefilledPartnerName(r.data.name);
          set("gp_id", r.data.id);
          set("gp_name_query", r.data.name);
        }
      } catch {}
    })();
    // eslint-disable-next-line
  }, [gpFromUrl]);

  // When Empfehlung is selected AND we have a URL gp → pre-fill empfehlung_name
  useEffect(() => {
    if (data.quelle === "Empfehlung" && prefilledPartnerName && !data.empfehlung_name) {
      set("empfehlung_name", prefilledPartnerName);
    }
    // eslint-disable-next-line
  }, [data.quelle, prefilledPartnerName]);

  const showEmpfehlung = data.quelle === "Empfehlung";
  // Steps: 0 intro · 1 name · 2 quelle · 3 (empfehlung?) · 4 alter · 5 beruf ·
  // 6 sicherheit · 7 bereiche · 8 wichtig · 9 motivation · 10 schon_getan ·
  // 11 investments · 12 einmalig · 13 monatlich · 14 kontakt · 15 erreichbar ·
  // 16 anmerkungen + partner + consent
  const totalSteps = showEmpfehlung ? 16 : 15;

  const canNext = useMemo(() => {
    switch (step) {
      case 0: return true;
      case 1: return data.name.trim().length >= 2;
      case 2: return !!data.quelle;
      case 3: return showEmpfehlung ? data.empfehlung_name.trim().length > 0 : true;
      case 4: return data.alter && Number(data.alter) >= 16 && Number(data.alter) <= 99;
      case 5: return data.beruf.trim().length >= 2;
      case 6: return !!data.sicherheit;
      case 7: return data.bereiche.length > 0;
      case 8: return data.wichtig.length > 0;
      case 9: return data.motivation.length > 0;
      case 10: return data.schon_getan.length > 0;
      case 11: return data.investments.length > 0;
      case 12: return data.einmalig_chf !== "" && Number(data.einmalig_chf) >= 0;
      case 13: return data.monatlich_chf !== "" && Number(data.monatlich_chf) >= 0;
      case 14: return /^\S+@\S+\.\S+$/.test(data.email) && data.telefon.trim().length >= 6;
      case 15: return !!data.erreichbar_zeitspanne;
      case 16: return data.datenschutz_akzeptiert && data.email_kontakt_ok;
      default: return false;
    }
  }, [step, data, showEmpfehlung]);

  const nextStep = () => {
    if (!showEmpfehlung && step === 2) setStep(4);
    else setStep((s) => Math.min(s + 1, 16));
  };
  const prevStep = () => {
    if (!showEmpfehlung && step === 4) setStep(2);
    else setStep((s) => Math.max(s - 1, 0));
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        gp_id: data.gp_id || undefined,
        gp_name_query: data.gp_name_query || undefined,
        name: data.name.trim(),
        quelle: data.quelle,
        empfehlung_name: data.empfehlung_name || "",
        alter: Number(data.alter),
        beruf: data.beruf.trim(),
        sicherheit: data.sicherheit,
        bereiche: data.bereiche,
        wichtig: data.wichtig,
        motivation: data.motivation,
        schon_getan: data.schon_getan,
        investments: data.investments,
        einmalig_chf: Number(data.einmalig_chf) || 0,
        monatlich_chf: Number(data.monatlich_chf) || 0,
        email: data.email.trim(),
        telefon: data.telefon.trim(),
        erreichbar_zeitspanne: data.erreichbar_zeitspanne,
        anmerkungen: data.anmerkungen || "",
        datenschutz_akzeptiert: data.datenschutz_akzeptiert,
        email_kontakt_ok: data.email_kontakt_ok,
      };
      const r = await axios.post(`${API}/api/finanzcheck/submit`, payload);
      setDone(r.data);
    } catch (e) {
      alert("Beim Absenden ist etwas schiefgelaufen. Bitte nochmal versuchen.\n" + (e?.response?.data?.detail || e.message));
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-ivory-100 flex items-center justify-center px-6 py-24">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          className="max-w-2xl w-full bg-white rounded-3xl border border-ink-700/10 shadow-sm p-10 text-center" data-testid="fc-success">
          <div className="w-16 h-16 mx-auto rounded-full bg-gold-500 flex items-center justify-center mb-6">
            <PartyPopper className="w-8 h-8 text-ink-900" />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-medium text-ink-900 mb-4">
            Danke, <span className="text-gold-gradient italic font-serif-display">{data.name.split(" ")[0]}</span>!
          </h1>
          <p className="text-ink-700 leading-relaxed mb-2">Deine Erstanalyse ist unterwegs an <strong>{data.email}</strong>.</p>
          <p className="text-ink-500 text-sm">Dein persönlicher Partner <strong>{done.partner_name}</strong> meldet sich zur angegebenen Zeitspanne bei dir.</p>
          <Link to="/" className="mt-8 inline-block rounded-full bg-ink-900 text-gold-300 px-6 py-3 text-sm font-medium ring-1 ring-gold-500/30">
            Zurück zur Startseite
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory-100 relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-gold-500/5 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-gold-500/5 blur-3xl pointer-events-none" />

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-16">
        <Link to="/" className="inline-flex items-center gap-3 mb-8" data-testid="fc-logo">
          <img src="/moonshine-logo.png" alt="" className="h-10 w-10 rounded-full ring-1 ring-gold-500/30 object-cover" />
          <div className="leading-tight">
            <div className="font-display font-medium text-ink-900 text-sm">Moonshine</div>
            <div className="font-sans text-[10px] uppercase tracking-[0.18em] text-ink-500">Solutions</div>
          </div>
        </Link>

        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-gold-700 mb-3">Vorsorge- & Finanzcheck</div>
          <Progress step={Math.max(1, step)} total={totalSteps} />
        </div>

        <div className="rounded-3xl bg-white border border-ink-700/10 shadow-sm p-6 sm:p-10 min-h-[420px]">
          <AnimatePresence mode="wait">
            {step === 0 && (
              <Q key="s0">
                <div className="w-14 h-14 rounded-2xl bg-ink-900 flex items-center justify-center mb-2">
                  <Sparkles className="w-6 h-6 text-gold-500" />
                </div>
                <QTitle>Dein persönlicher Vorsorge- & <span className="italic font-serif-display text-gold-gradient">Finanzcheck.</span></QTitle>
                <QHint>Finde in wenigen Minuten heraus, wie gut du aktuell abgesichert bist – und wo noch echtes Potenzial liegt.</QHint>
                <QHint>Dauer: ca. 3 Minuten. Vertraulich.</QHint>
              </Q>
            )}

            {step === 1 && (
              <Q key="s1">
                <QTitle>Wie ist dein Name? <span className="text-ink-500 text-base">(Vor- und Nachname)</span></QTitle>
                <input className={inputCls} autoFocus placeholder="z. B. Max Muster"
                  value={data.name} onChange={(e) => set("name", e.target.value)} data-testid="fc-name" />
              </Q>
            )}

            {step === 2 && (
              <Q key="s2">
                <QTitle>Wie bist du auf diesen Vorsorgecheck gestossen{data.name && `, ${data.name.split(" ")[0]}`}?</QTitle>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {QUELLE_OPTS.map((o) => (
                    <IconChip key={o.v} icon={o.icon} label={o.label}
                      active={data.quelle === o.v} onClick={() => set("quelle", o.v)}
                      testid={`fc-quelle-${o.v.toLowerCase()}`} />
                  ))}
                </div>
              </Q>
            )}

            {step === 3 && showEmpfehlung && (
              <Q key="s3">
                <QTitle>Wer hat dir den Check empfohlen? <span className="text-ink-500 text-base">(Vor- und Nachname)</span></QTitle>
                <p className="text-xs text-ink-500 -mt-2">Tippe die ersten zwei Buchstaben — passende Geschäftspartner werden dir vorgeschlagen.</p>
                <PartnerPicker
                  data={data}
                  set={set}
                  nameField="empfehlung_name"
                  idField="gp_id"
                  placeholder="z. B. Pascal Schwab"
                  testIdBase="fc-empfehlung"
                />
                {prefilledPartnerName && (
                  <p className="text-xs text-gold-700">Automatisch übernommen aus deinem Link.</p>
                )}
              </Q>
            )}

            {step === 4 && (
              <Q key="s4">
                <QTitle>Wie alt bist du?</QTitle>
                <input type="number" min="16" max="99" className={inputCls + " max-w-[200px]"}
                  autoFocus placeholder="z. B. 34" value={data.alter}
                  onChange={(e) => set("alter", e.target.value)} data-testid="fc-alter" />
              </Q>
            )}

            {step === 5 && (
              <Q key="s5">
                <QTitle>Was machst du beruflich?</QTitle>
                <QHint>Zum Beispiel: Projektleiterin, Elektriker, Unternehmerin, Student …</QHint>
                <div className="relative">
                  <Briefcase className="w-4 h-4 absolute left-5 top-1/2 -translate-y-1/2 text-ink-500" />
                  <input className={inputCls + " pl-12"} autoFocus placeholder="Beruf / Tätigkeit"
                    value={data.beruf} onChange={(e) => set("beruf", e.target.value)} data-testid="fc-beruf" />
                </div>
              </Q>
            )}

            {step === 6 && (
              <Q key="s6">
                <QTitle>Wie sicher fühlst du dich aktuell in Bezug auf deine finanzielle Zukunft?</QTitle>
                <div className="grid gap-3">
                  {SICHERHEIT_OPTS.map((o) => (
                    <Chip key={o.v} active={data.sicherheit === o.v}
                      onClick={() => set("sicherheit", o.v)} testid={`fc-sicherheit-${o.v.slice(0,4)}`}>
                      <span className="flex items-center gap-3">
                        <span className={`w-3 h-3 rounded-full ${o.color}`} />
                        <span className="font-medium">{o.v}</span>
                      </span>
                    </Chip>
                  ))}
                </div>
              </Q>
            )}

            {step === 7 && (
              <Q key="s7">
                <QTitle>Welche Bereiche möchtest du optimieren?</QTitle>
                <QHint>Mehrfachauswahl möglich.</QHint>
                <div className="grid sm:grid-cols-2 gap-3">
                  {BEREICHE_OPTS.map((o) => (
                    <Chip key={o} active={data.bereiche.includes(o)}
                      onClick={() => toggle("bereiche", o)} testid={`fc-b-${o.slice(0,4)}`}>{o}</Chip>
                  ))}
                </div>
              </Q>
            )}

            {step === 8 && (
              <Q key="s8">
                <QTitle>Was ist dir aktuell am wichtigsten?</QTitle>
                <QHint>Mehrfachauswahl möglich.</QHint>
                <div className="grid sm:grid-cols-2 gap-3">
                  {WICHTIG_OPTS.map((o) => (
                    <Chip key={o} active={data.wichtig.includes(o)}
                      onClick={() => toggle("wichtig", o)} testid={`fc-w-${o.slice(2,6)}`}>{o}</Chip>
                  ))}
                </div>
              </Q>
            )}

            {step === 9 && (
              <Q key="s9">
                <QTitle>Was motiviert dich, deine Finanzen zu verbessern?</QTitle>
                <QHint>Mehrfachauswahl möglich.</QHint>
                <div className="grid sm:grid-cols-2 gap-3">
                  {MOTIVATION_OPTS.map((o) => (
                    <Chip key={o} active={data.motivation.includes(o)}
                      onClick={() => toggle("motivation", o)} testid={`fc-m-${o.slice(2,6)}`}>{o}</Chip>
                  ))}
                </div>
              </Q>
            )}

            {step === 10 && (
              <Q key="s10">
                <QTitle>Was hast du bereits getan, um für deine Zukunft vorzusorgen?</QTitle>
                <QHint>Mehrfachauswahl möglich.</QHint>
                <div className="grid sm:grid-cols-2 gap-3">
                  {GETAN_OPTS.map((o) => (
                    <Chip key={o} active={data.schon_getan.includes(o)}
                      onClick={() => toggle("schon_getan", o)} testid={`fc-g-${o.slice(0,4)}`}>{o}</Chip>
                  ))}
                </div>
              </Q>
            )}

            {step === 11 && (
              <Q key="s11">
                <QTitle>Falls du bereits investierst – in was?</QTitle>
                <QHint>Mehrfachauswahl möglich.</QHint>
                <div className="grid sm:grid-cols-2 gap-3">
                  {INVEST_OPTS.map((o) => (
                    <Chip key={o} active={data.investments.includes(o)}
                      onClick={() => toggle("investments", o)} testid={`fc-i-${o.slice(0,4)}`}>{o}</Chip>
                  ))}
                </div>
              </Q>
            )}

            {step === 12 && (
              <Q key="s12">
                <QTitle>Gibt es aktuell einen einmaligen Betrag, den du für deine Geldanlage einsetzen könntest?</QTitle>
                <QHint>Zum Beispiel Ersparnisse, die bereits vorhanden sind oder nicht aktiv gebraucht werden. Eine grobe Einschätzung reicht.</QHint>
                <div className="relative max-w-sm">
                  <input type="number" min="0" step="100" className={inputCls + " pr-16"}
                    autoFocus placeholder="z. B. 15000" value={data.einmalig_chf}
                    onChange={(e) => set("einmalig_chf", e.target.value)} data-testid="fc-einmalig" />
                  <span className="absolute right-5 top-1/2 -translate-y-1/2 text-ink-500 text-sm font-medium">CHF</span>
                </div>
              </Q>
            )}

            {step === 13 && (
              <Q key="s13">
                <QTitle>Gibt es eine monatliche Summe, die du dir vorstellen könntest, regelmässig zu investieren?</QTitle>
                <QHint>Auch hier reicht eine grobe Schätzung.</QHint>
                <div className="relative max-w-sm">
                  <input type="number" min="0" step="50" className={inputCls + " pr-16"}
                    autoFocus placeholder="z. B. 500" value={data.monatlich_chf}
                    onChange={(e) => set("monatlich_chf", e.target.value)} data-testid="fc-monatlich" />
                  <span className="absolute right-5 top-1/2 -translate-y-1/2 text-ink-500 text-sm font-medium">CHF / Monat</span>
                </div>
              </Q>
            )}

            {step === 14 && (
              <Q key="s14">
                <QTitle>Wie erreichen wir dich?</QTitle>
                <QHint>Deine Erstanalyse geht direkt an diese E-Mail.</QHint>
                <div className="space-y-4">
                  <div className="relative">
                    <Mail className="w-4 h-4 absolute left-5 top-1/2 -translate-y-1/2 text-ink-500" />
                    <input type="email" className={inputCls + " pl-12"} placeholder="deine@email.ch"
                      value={data.email} onChange={(e) => set("email", e.target.value)} data-testid="fc-email" />
                  </div>
                  <div className="relative">
                    <Phone className="w-4 h-4 absolute left-5 top-1/2 -translate-y-1/2 text-ink-500" />
                    <input type="tel" className={inputCls + " pl-12"} placeholder="+41 79 000 00 00"
                      value={data.telefon} onChange={(e) => set("telefon", e.target.value)} data-testid="fc-telefon" />
                  </div>
                </div>
              </Q>
            )}

            {step === 15 && (
              <Q key="s15">
                <QTitle>Wann bist du telefonisch am besten erreichbar?</QTitle>
                <QHint>3–5 Minuten reichen – ein entspanntes Kennenlernen, ohne Verpflichtung.</QHint>
                <div className="grid sm:grid-cols-2 gap-3">
                  {ERREICHBAR_OPTS.map((o) => (
                    <IconChip key={o.v} icon={o.icon} label={o.label} sub={o.sub}
                      active={data.erreichbar_zeitspanne === o.v}
                      onClick={() => set("erreichbar_zeitspanne", o.v)}
                      testid={`fc-erreichbar-${o.label.toLowerCase().slice(0,4)}`} />
                  ))}
                </div>
              </Q>
            )}

            {step === 16 && (
              <Q key="s16">
                <QTitle>Fast fertig.</QTitle>
                {!gpFromUrl && (
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-ink-900">Kennst du deinen persönlichen Partner bei Moonshine?</label>
                    <PartnerPicker data={data} set={set} />
                  </div>
                )}
                <div className="space-y-2 mt-6">
                  <label className="text-sm font-medium text-ink-900">Noch Anmerkungen oder offene Fragen? (optional)</label>
                  <textarea rows={4} className={inputCls + " resize-none"}
                    placeholder="Alles, was du uns noch mit auf den Weg geben möchtest …"
                    value={data.anmerkungen} onChange={(e) => set("anmerkungen", e.target.value)}
                    data-testid="fc-anmerkungen" />
                </div>

                <div className="space-y-3 mt-6">
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="fc-consent-privacy">
                    <input type="checkbox" checked={data.datenschutz_akzeptiert}
                      onChange={(e) => set("datenschutz_akzeptiert", e.target.checked)}
                      className="mt-1 accent-gold-600 w-4 h-4" />
                    <span className="text-sm text-ink-700">
                      Ich akzeptiere die <a href="https://finanzcheck.moonshine-solutions.com/datenschutz"
                        target="_blank" rel="noopener noreferrer"
                        className="underline text-gold-700 hover:text-gold-600">Datenschutzverordnung</a>.
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="fc-consent-email">
                    <input type="checkbox" checked={data.email_kontakt_ok}
                      onChange={(e) => set("email_kontakt_ok", e.target.checked)}
                      className="mt-1 accent-gold-600 w-4 h-4" />
                    <span className="text-sm text-ink-700">
                      Ich bin damit einverstanden, per E-Mail kontaktiert zu werden. (Die Erstanalyse kommt per Mail.)
                    </span>
                  </label>
                </div>

                <div className="rounded-2xl bg-ivory-100 border border-gold-500/20 p-4 text-sm text-ink-700 flex gap-3 items-start mt-6">
                  <ShieldCheck className="w-5 h-5 text-gold-700 flex-shrink-0 mt-0.5" />
                  <span>Deine Daten sind bei uns sicher. Wir versenden dir eine persönliche Erstanalyse direkt per E-Mail.</span>
                </div>
              </Q>
            )}
          </AnimatePresence>
        </div>

        <div className="mt-6 flex items-center justify-between gap-3">
          {step > 0 ? (
            <button onClick={prevStep} type="button"
              className="inline-flex items-center gap-2 text-ink-500 hover:text-ink-900 text-sm transition-colors"
              data-testid="fc-back">
              <ArrowLeft className="w-4 h-4" /> Zurück
            </button>
          ) : <span />}

          {step < 16 && (
            <button onClick={nextStep} type="button" disabled={!canNext}
              className="inline-flex items-center gap-2 rounded-full bg-ink-900 hover:bg-ink-700 text-gold-300 px-6 py-3 text-sm font-medium ring-1 ring-gold-500/30 disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:-translate-y-0.5"
              data-testid="fc-next">
              {step === 0 ? "Los geht's" : "Weiter"} <ArrowRight className="w-4 h-4" />
            </button>
          )}
          {step === 16 && (
            <button onClick={submit} type="button" disabled={submitting || !canNext}
              className="inline-flex items-center gap-2 rounded-full bg-gold-500 hover:bg-gold-400 text-ink-900 px-7 py-3.5 text-sm font-semibold disabled:opacity-60 transition-all hover:-translate-y-0.5"
              data-testid="fc-submit">
              {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Wird gesendet …</>) : (<>Analyse anfordern <ArrowRight className="w-4 h-4" /></>)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
